import type { Tab } from './index';
import { esc, timeAgo } from '../utils';
import { workerFetch } from '../worker';

// Agent Ops — the paper sleeve's cumulative equity curve vs SPY, indexed to 100 on the $6k
// sleeve cap. Data is computed on the host (agent-equity.mjs from ledger.jsonl) and served
// from KV at /api/agent-equity. Canvas chart + hover tooltip mirror the Performance tab.

interface EqPoint { date: string; equityIdx: number; spyIdx: number; equity: number; realized: number; unreal: number; deployed: number; open: string[]; events: string[]; }
interface EqTrade { ticker: string; strategy: string; qty: number; entry: number; exit: number | null; retPct: number | null; holdingDays: number | null; open: boolean; }
interface EqSummary { cumReturnPct: number; spyReturnPct: number; alphaPct: number; realizedPnl: number; closed: number; wins: number; winRate: number | null; openCount: number; deployed: number; maxDrawdownPct: number; }
interface AgentEquityResp { base: number; updatedAt?: string; startDate?: string; points: EqPoint[]; summary?: EqSummary; trades?: EqTrade[]; note?: string; }

const SLEEVE = '#00ff9d';      // sleeve line (green)
const SPYCOL = 'rgba(122,157,181,0.9)';

export class AgentOpsTab implements Tab {
  readonly id = 'agentops';
  private loaded = false;
  private data: AgentEquityResp | null = null;

  private el(id: string): HTMLElement { return document.getElementById(id) as HTMLElement; }

  init(): void {
    this.el('ae-refresh')?.addEventListener('click', () => { this.loaded = false; this.load(); });
    window.addEventListener('resize', () => { if (this.data) this.renderChart(); });
  }
  render(): void { if (!this.loaded) this.load(); }

  private async load(): Promise<void> {
    const loading = this.el('ae-loading'), error = this.el('ae-error');
    loading.style.display = 'block'; error.style.display = 'none';
    try {
      const d = await workerFetch<AgentEquityResp>('/api/agent-equity');
      loading.style.display = 'none';
      if (!d.points?.length) {
        error.style.display = 'block';
        error.textContent = d.note ?? 'No equity data yet — the recorder pushes after each daily agent run.';
        this.loaded = true; return;
      }
      this.data = d;
      this.el('ae-updated').textContent = d.updatedAt ? `Updated ${timeAgo(d.updatedAt)} · base $${(d.base ?? 6000).toLocaleString()} sleeve` : '';
      this.fillStats(d.summary);
      this.renderChart();
      this.renderTrades(d.trades ?? []);
      this.loaded = true;
    } catch (err: unknown) {
      loading.style.display = 'none'; error.style.display = 'block';
      error.textContent = `Error: ${err instanceof Error ? err.message : String(err)}`;
    }
  }

  private fillStats(s?: EqSummary): void {
    if (!s) return;
    const pct = (id: string, v: number, color = true) => {
      const e = this.el(id); if (!e) return;
      e.textContent = `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
      if (color) e.style.color = v >= 0 ? 'var(--green)' : 'var(--red)';
    };
    pct('ae-stat-cum', s.cumReturnPct);
    pct('ae-stat-spy', s.spyReturnPct);
    pct('ae-stat-alpha', s.alphaPct);
    pct('ae-stat-dd', s.maxDrawdownPct);
    const realized = this.el('ae-stat-realized');
    if (realized) { realized.textContent = `${s.realizedPnl >= 0 ? '+' : '-'}$${Math.abs(s.realizedPnl).toFixed(0)}`; realized.style.color = s.realizedPnl >= 0 ? 'var(--green)' : 'var(--red)'; }
    const wr = this.el('ae-stat-winrate');
    if (wr) wr.textContent = s.winRate != null ? `${s.winRate}% (${s.wins}/${s.closed})` : '—';
    const op = this.el('ae-stat-open');
    if (op) op.textContent = `${s.openCount} · $${s.deployed.toLocaleString()}`;
  }

  private renderChart(): void {
    const d = this.data; if (!d?.points?.length) return;
    const canvas = this.el('ae-canvas') as HTMLCanvasElement;
    const pts = d.points;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.parentElement!.getBoundingClientRect();
    const W = rect.width, H = 420;
    canvas.width = W * dpr; canvas.height = H * dpr;
    canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
    const ctx = canvas.getContext('2d')!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const margin = { top: 20, right: 20, bottom: 40, left: 52 };
    const iW = W - margin.left - margin.right, iH = H - margin.top - margin.bottom;
    const n = pts.length;
    const vals = [...pts.map(p => p.equityIdx), ...pts.map(p => p.spyIdx), 100];
    const minV = Math.min(...vals) - 0.4, maxV = Math.max(...vals) + 0.4;
    const xScale = (i: number) => margin.left + (n === 1 ? iW / 2 : i / (n - 1) * iW);
    const yScale = (v: number) => margin.top + (1 - (v - minV) / (maxV - minV)) * iH;

    const dl = (key: 'equityIdx' | 'spyIdx', color: string, width: number, dash: number[] = []) => {
      ctx.strokeStyle = color; ctx.lineWidth = width; ctx.setLineDash(dash);
      ctx.beginPath(); pts.forEach((p, i) => { const x = xScale(i), y = yScale(p[key]); i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y); }); ctx.stroke(); ctx.setLineDash([]);
    };
    const drawStatic = () => {
      ctx.clearRect(0, 0, W, H);
      // gridlines + y labels
      ctx.strokeStyle = 'rgba(30,52,72,0.5)'; ctx.setLineDash([3, 3]);
      for (let i = 0; i <= 5; i++) {
        const y = margin.top + iH * i / 5;
        ctx.beginPath(); ctx.moveTo(margin.left, y); ctx.lineTo(margin.left + iW, y); ctx.stroke();
        ctx.fillStyle = '#3d6478'; ctx.font = '10px Space Mono,monospace'; ctx.textAlign = 'right';
        ctx.fillText((maxV - (maxV - minV) * i / 5).toFixed(1), margin.left - 6, y + 4);
      }
      ctx.setLineDash([]);
      // 100 baseline
      const y100 = yScale(100);
      ctx.strokeStyle = 'rgba(122,157,181,0.35)'; ctx.setLineDash([5, 5]);
      ctx.beginPath(); ctx.moveTo(margin.left, y100); ctx.lineTo(margin.left + iW, y100); ctx.stroke(); ctx.setLineDash([]);
      // x date labels
      ctx.fillStyle = '#3d6478'; ctx.font = '10px Space Mono,monospace'; ctx.textAlign = 'center';
      const step = Math.max(1, Math.floor(n / 6));
      pts.forEach((p, i) => {
        if (i % step === 0 || i === n - 1) {
          const dt = new Date(p.date + 'T00:00:00');
          ctx.fillText(dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), xScale(i), H - margin.bottom + 15);
        }
      });
      dl('spyIdx', SPYCOL, 2, [5, 3]);
      dl('equityIdx', SLEEVE, 2.5);
    };
    drawStatic();

    canvas.onmousemove = (e: MouseEvent) => {
      const r = canvas.getBoundingClientRect();
      const mx = e.clientX - r.left;
      const i = Math.max(0, Math.min(n - 1, Math.round((mx - margin.left) / (iW || 1) * (n - 1))));
      const p = pts[i];
      const tip = this.el('ae-tooltip'); if (!tip) return;
      const sleeve = p.equityIdx - 100, spy = p.spyIdx - 100, alpha = sleeve - spy;
      const ev = p.events?.length ? `<div style="color:var(--text3);font-size:10px;margin-top:5px;border-top:1px solid var(--border);padding-top:4px;">${p.events.map(esc).join('<br>')}</div>` : '';
      tip.style.display = 'block';
      tip.innerHTML = `<div style="color:var(--text3);font-size:10px;margin-bottom:6px;">${esc(p.date)}</div>
        <div style="color:${SLEEVE};margin-bottom:3px;">Sleeve: <strong>${sleeve >= 0 ? '+' : ''}${sleeve.toFixed(2)}%</strong></div>
        <div style="color:${SPYCOL};">S&amp;P 500: <strong>${spy >= 0 ? '+' : ''}${spy.toFixed(2)}%</strong></div>
        <div style="color:${alpha >= 0 ? 'var(--green)' : 'var(--red)'};font-size:10px;margin-top:4px;">Alpha: ${alpha >= 0 ? '+' : ''}${alpha.toFixed(2)}%</div>${ev}`;
      let tx = mx + 15, ty = e.clientY - r.top - 10;
      if (tx + 210 > W) tx -= 220;
      tip.style.left = tx + 'px'; tip.style.top = ty + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      drawStatic();
      const cx = xScale(i);
      ctx.strokeStyle = 'rgba(0,212,255,0.3)'; ctx.lineWidth = 1; ctx.setLineDash([3, 3]);
      ctx.beginPath(); ctx.moveTo(cx, margin.top); ctx.lineTo(cx, margin.top + iH); ctx.stroke(); ctx.setLineDash([]);
      ctx.fillStyle = SLEEVE; ctx.beginPath(); ctx.arc(cx, yScale(p.equityIdx), 3, 0, 7); ctx.fill();
    };
    canvas.onmouseleave = () => { const tip = this.el('ae-tooltip'); if (tip) tip.style.display = 'none'; };
  }

  private renderTrades(trades: EqTrade[]): void {
    const host = this.el('ae-trades'); if (!host) return;
    if (!trades.length) { host.innerHTML = ''; return; }
    const th = (t: string) => `<th style="padding:8px 12px;text-align:left;font-size:10px;letter-spacing:0.1em;color:var(--text3);text-transform:uppercase;">${t}</th>`;
    const rows = trades.slice().sort((a, b) => Number(a.open) - Number(b.open)).map(t => {
      const ret = t.retPct;
      const retCell = t.open
        ? `<span style="color:var(--accent);font-size:11px;">OPEN</span>`
        : `<span style="color:${(ret ?? 0) >= 0 ? 'var(--green)' : 'var(--red)'};font-weight:700;">${(ret ?? 0) >= 0 ? '+' : ''}${(ret ?? 0).toFixed(2)}%</span>`;
      return `<tr style="border-bottom:1px solid var(--border);">
        <td style="padding:9px 12px;font-family:var(--mono);font-weight:700;color:var(--accent);">${esc(t.ticker)}</td>
        <td style="padding:9px 12px;font-size:11px;color:var(--text3);">${esc(t.strategy)}</td>
        <td style="padding:9px 12px;font-family:var(--mono);font-size:11px;">${t.qty} @ $${t.entry.toFixed(2)}</td>
        <td style="padding:9px 12px;font-family:var(--mono);font-size:11px;">${t.exit != null ? '$' + t.exit.toFixed(2) : '—'}</td>
        <td style="padding:9px 12px;font-family:var(--mono);font-size:11px;color:var(--text3);">${t.holdingDays != null ? t.holdingDays + 'd' : '—'}</td>
        <td style="padding:9px 12px;font-family:var(--mono);">${retCell}</td>
      </tr>`;
    }).join('');
    host.innerHTML = `<table style="width:100%;border-collapse:collapse;">
      <thead><tr style="border-bottom:2px solid var(--border);">${th('Ticker')}${th('Strategy')}${th('Entry')}${th('Exit')}${th('Held')}${th('Return')}</tr></thead>
      <tbody>${rows}</tbody></table>`;
  }
}
